import {Component} from '@angular/core';
import {SearchForm} from '../../../sdk/component/search/searchForm';
import {Router} from '@angular/router';
import {SearchService} from '../../../sdk/component/search/search.service';
import {ROUTES} from '../../../app.routes';
import {Account, AccountsSortField, BillingType, FileTransfer, WebAccountService} from 'web-angular-client';
import {saveAs} from 'file-saver';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {CancelPaymentService} from '../cancel-payment/cancel-payment.service';
import {AccountStatusUtil} from '../../../sdk/interface/accountStatusUtil';
import {AlertService} from '../../../sdk/service/alert.service';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {MenuItem} from 'primeng/api';
import {ContextMenu} from 'primeng/contextmenu';
import {ConfirmationService} from '../../../sdk/service/confirmation.service';
import {Helper} from '../../../sdk/util/helper';
import {downloadInvoice} from '../../../sdk/util/invoice-download.helper';

@Component({
    selector: 'app-payment-request-list',
    templateUrl: './payment-request-list.component.html',
    styleUrls: ['./payment-request-list.component.scss'],
    standalone: false
})
export class PaymentRequestListComponent extends SearchForm
{
    displayBatchNumber: boolean = false;
    sortFields: SortField[];
    selectedSortField: SortField | null;
    contextMenuItems: MenuItem[];
    selectedAccount: Account = null;
    openContextMenus: ContextMenu[] = [];

    constructor(
        private router: Router,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        private webAccountService: WebAccountService,
        protected searchService: SearchService,
        private ngxLoader: NgxUiLoaderService,
        protected cancelPaymentService: CancelPaymentService,
        protected accountStatusUtil: AccountStatusUtil,
        private alertService: AlertService,
        private confirmationService: ConfirmationService)
    {
        super(authenticationService, webAccountService, searchService, environmentService);
        this.sortFields = [
            {id: 'dateTimeCreated', name: 'Date Created'},
            {id: 'accountNumber', name: 'Account Number'},
            {id: 'referenceNumber', name: 'Reference'},
            {id: 'customer', name: 'Name'},
            {id: 'amount', name: 'Amount'},
            {id: 'dateTimePaid', name: 'Date Paid'}
        ];

        this.refreshContextMenuItems();
    }

    get contents(): [Account]
    {
        return this.results?.content;
    }

    refreshContextMenuItems()
    {
        this.contextMenuItems = [
            {
                label: 'Invoice', icon: 'fa fa-download fa-lg"', command: event => this.download(this.selectedAccount),
                disabled: this.selectedAccount ? !this.isInvoiceBillingType(this.selectedAccount.billingType) : false
            },
            {
                label: 'Edit', icon: 'fa fa-edit fa-lg', command: event => this.edit(this.selectedAccount),
                disabled: this.selectedAccount ? !this.getAccountStatusUtil().isPaymentOutstanding(this.selectedAccount.accountState) : false
            },
            {
                label: 'Send',
                icon: 'fa fa-send fa-lg',
                command: event => this.send(this.selectedAccount),
                disabled: this.selectedAccount ? !this.getAccountStatusUtil().isPaymentOutstanding(this.selectedAccount.accountState) : false
            },
            {
                label: 'Cancel', icon: 'fa fa-trash fa-lg', command: event => this.cancel(this.selectedAccount),
                disabled: this.selectedAccount ? !this.getAccountStatusUtil().isPaymentOutstanding(this.selectedAccount.accountState) : false
            }
        ];
    }

    create()
    {
        // not used but required to be implemented
    }

    view(account: Account)
    {
        if (this.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }

        this.router.navigate([ROUTES.SECURE.ACCOUNT.VIEW.pathSansParameters, account.id]).then();
    }

    getDefaultSortField(): string
    {
        return AccountsSortField.ID;
    }

    send(account: Account)
    {
        if (this.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }

        this.router.navigate([ROUTES.SECURE.ACCOUNT.SEND.pathSansParameters, account.id]).then();
    }

    edit(account: Account)
    {
        if (this.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }

        this.router.navigate([ROUTES.SECURE.ACCOUNT.EDIT.pathSansParameters, account.id]).then();
    }

    download(account: Account)
    {
        if (this.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }

        downloadInvoice(
            account.id,
            {
                invoiceLoader: id => this.webAccountService.getInvoiceByAccountId(id),
                onStart: () => this.ngxLoader.start(),
                onComplete: () => this.ngxLoader.stop(),
                onError: message => this.alertService.error(message)
            });
    }

    cancel(account: Account)
    {
        if (this.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }

        this.cancelPaymentService.confirm(account.customer, JSON.stringify(account.amount))
            .then((shouldCancel: Boolean) =>
            {
                if (shouldCancel)
                {
                    this.webAccountService.cancelAccount(account.id).subscribe((a) =>
                        this.router.navigate([ROUTES.SECURE.ACCOUNT.VIEW.pathSansParameters, a.id]));
                }
            });
    }

    /**
     * @return <i>true</i> if {@link BillingType} is INVOICE or null.
     * Invoice is the default type, so a <i>null</i> value always defaults to invoice.
     */
    isInvoiceBillingType(billingType: BillingType): boolean
    {
        return (billingType === null || billingType === BillingType.INVOICE);
    }

    public getAccountStatusUtil(): AccountStatusUtil
    {
        return this.accountStatusUtil;
    }

    updateDisplayBatchNumber(displayBatchNumber: boolean)
    {
        this.displayBatchNumber = displayBatchNumber;
    }

    getAmount(account: Account): number
    {
        return !!account.dateTimePaid ? account.amountPaid : account.amount;
    }

    openMenu(event: MouseEvent, contextMenu: ContextMenu, account: Account)
    {
        this.closeExistingContextMenus();

        if (this.isGuestAdminUser())
        {
            this.displayGuestPrivilegeError();
            return;
        }

        event.stopPropagation();
        event.preventDefault();

        this.selectedAccount = account;

        this.refreshContextMenuItems();

        contextMenu.show(event);

        this.openContextMenus.push(contextMenu);
    }

    /**
     * Exports the request to pays to a file. If the date created filter is set to older than 6 months or if it is not
     * populated, a confirmation box is provided to the user indicating that the start date will change to 6 Months
     * prior to todays date. If confirmed, the file will be downloaded.
     * */
    exportRequestToPays()
    {
        if (this.isDateCreatedLowerWithinSixMonths())
        {
            this.getAccountListFile();
        }
        else
        {
            this.confirmationService.confirm(
                'Export Request to Pays',
                'The \'Date created from\' filter value is not populated or is older than 6 months. ' +
                'Please note that request to pays created older than 6 months ago will not be added to this report. ' +
                'Do you wish to continue?')
                .then((confirmed) =>
                {
                    if (confirmed)
                    {
                        this.getAccountListFile();
                    }
                });
        }
    }

    getExportRequestsToPayTooltipText()
    {
        return 'Export requests to pay to a CSV file based on the search criteria above.' +
            'If no criteria is set, all requests from the past 6 months will be exported.' +
            'If the \'From\' date is not set or is older than 6 months, it will default to 6 months ago.';
    }

    /**
     * Retrieves the accountList file based on the search criteria passed in.
     * */
    private getAccountListFile()
    {
        this.ngxLoader.start();

        const searchCriteria = {
            ...this.searchService.searchableCriteria,
            amountUpper: Helper.convertToMinorDenomination(this.searchService?.searchableCriteria?.amountUpper)?.toString() ?? null,
            amountLower: Helper.convertToMinorDenomination(this.searchService?.searchableCriteria?.amountLower)?.toString() ?? null
        };

        this.webAccountService.getAccountListFile(
            this.searchService.field ?? this.getDefaultSortField(),
            Direction[this.searchService.direction] ?? 'ASC',
            searchCriteria).subscribe({
            next: (fileTransferDTO: FileTransfer) =>
            {
                this.ngxLoader.stop();

                if (null != fileTransferDTO.fileContent)
                {
                    let contents = atob(fileTransferDTO.fileContent);
                    let blob = new Blob(contents.split(''), {type: 'text/csv;charset=utf-8'});
                    saveAs(blob, `${fileTransferDTO.fileName}.${fileTransferDTO.fileExtension}`);
                }
                else
                {
                    this.alertService.info('No requests to pay available for download.');
                }
            },
            error: () =>
            {
                this.ngxLoader.stop();
                this.alertService.error(
                    'Failed to export requests to pay to file, please contact support for assistance');
            }
        });
    }

    private closeExistingContextMenus()
    {
        this.openContextMenus.forEach(menu => menu.hide());
        this.openContextMenus = [];
    }

    private displayGuestPrivilegeError(): void
    {
        this.alertService.error('Guest accounts cannot perform this action.', false);
    }

    /**
     * Checks if the selected filter date is older than 6 months or if it is not selected.
     * The result is returned to the calling method which determines if a confirmation message should be shown to the
     * user or not.
     * */
    private isDateCreatedLowerWithinSixMonths(): boolean
    {
        if (!this.searchService.searchableCriteria.dateTimeCreatedLower)
        {
            return false;
        }

        const selectedDateTimeCreatedLower = new Date(this.searchService.searchableCriteria.dateTimeCreatedLower);
        const dateThreshold = new Date();

        dateThreshold.setMonth(new Date().getMonth() - 6);

        return selectedDateTimeCreatedLower > dateThreshold;
    }
}

interface SortField
{
    id: string;
    name: string;
}

enum Direction
{
    ASC = 'ASC',
    DESC = 'DESC'
}
